import {useState, useRef, useMemo, useEffect} from 'react';
import Select from 'react-select';
import {useTranslation} from 'react-i18next';
import {Helmet} from 'react-helmet-async';
import {useDispatch, useSelector} from 'react-redux';
import SwitchSelector from 'react-switch-selector';

import {StyledReport, ReportTabs} from '../report.styled';
import {newSelectStyles} from '../../../common/shared/select.styled';
import {useAccessToken} from '../../../common/hooks/useAccessToken';
import {hideLoader, showLoader} from '../../../store/common/slice';
import {fetchBuildingData, fetchPaymentsByYear, getBuildingTitle} from '../../../store/buildings/actions';
import { changeTab } from '../../../store/manager/slice';

import { constants } from '../../../common/constants/constants';
import {BllinkLogger} from '../../../common/utils/bllink_loggers';

import user from '../../../images/newDesign/user.svg';
import Search from '../../../images/search.svg';
import PrintPdfDropdown from '../../../common/export_documents/PrintPdfDropdown';
import CommentsHistory from '../../comments/CommentsHistory';
import MonthlyData from './MonthlyData';
import OneTimeData from './OneTimeData';
import FullReport from './FullReport';
import TenantDetailsModal from './TenantDetailsModal';
import BllinkUtils from '../../../common/utils/utils';
import useBodyClass from '../../../common/hooks/useBodyClassName';
import { shortenText, calcMonthsTextForSelectBox } from '../../../common/shared/statelessMethods';
import { isAdminSelector } from '../../../store/auth/selectors';
import { PageTitle } from '../../../common/PageTitle';

const BllinkTime = BllinkUtils.BllinkTime;

export default function ApartmentReport({match, history, address}) {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const pdfContainer = useRef();
    const client = useAccessToken();
    const userCheck = useSelector((state) => state.auth.userCheck);
    const currentBuildingData = useSelector(state => state.buildings.currentBuildingData);
    const ltr = useSelector((state) => state.common.ltrEnabled);
    const tab = useSelector(state => state.manager.tab);
    const isAdmin = useSelector(isAdminSelector);

    const {buildingID, apartmentNumber, year} = match.params;

    const [rows, setRows] = useState([]);
    const [filters, setFilters] = useState({});
    const [selectedApartment, setSelectedApartment] = useState({});
    const [apartmentOptions, setApartmentOptions] = useState([]);
    const [currentTab, setCurrentTab] = useState(tab || 'full');
    const [showTenantDetails, setShowTenantsDetails] = useState(false);
    const [monthlyOneTimeData, setMonthlyOneTimeData] = useState({});
    const [monthlyOneTimeForApt, setMonthlyOneTimeForApt] = useState({});
    const [parsedData, setParsedData] = useState([]);
    const [keysToIgnore, setKeysToIgnore] = useState([]);
    const [titleRow, setTitleRow] = useState([]);

    useBodyClass('report');

    const fullData = useMemo(() => rows, [rows]);

    useEffect(() => {
        if (fullData && fullData.length > 0) {
            const keys = ['PaymentDate', 'ClientName', 'Amount', 'CardLast4Digits', 'TenantType', 'paymentFor', 'invoiceDownloadLink', 'Comment', 'Method'];
            const allKeys = fullData.flatMap(item => Object.keys(item)).filter((key, i, a) => a.indexOf(key) === i);
            const keysToIgnore = allKeys?.filter(item => !keys.includes(item));
            setKeysToIgnore(keysToIgnore);

            const parsedRows = JSON.parse(JSON.stringify(fullData));
            parsedRows.map(row => {
                row.PaymentDate = BllinkTime.parseDate(row.PaymentDate);
                row.TenantType = t([`reports.${row.TenantType}`, 'reports.tenantTypeUnknown']);
                const { months, oneTime } = row;
                let text = '';
                if (Array.isArray(months)) text = shortenText(calcMonthsTextForSelectBox(months));
                if (oneTime) text = shortenText(oneTime.description);
                row.paymentFor = text.shortComment || text;
                let comment = shortenText(row.Comment);
                row.Comment = comment?.shortComment || comment || '';
                row.Method = row.translatedMethod;
                return parsedRows
            });
            parsedRows.map(row => {
                keysToIgnore.forEach(column => {
                    delete row[column];
                })
                return parsedRows;
            });
            setParsedData(parsedRows);
            setTitleRow([{
                PaymentDate: t('reports.billing_date'),
                ClientName: t('reports.tenantName'),
                Amount: t('reports.total_to_pay'),
                CardLast4Digits: t('reports.4_last_digits'),
                TenantType: t('reports.ownership_type'),
                paymentFor: t('reports.payment_for'),
                invoiceDownloadLink: t('payments.confirmation_id'),
                Comment: t('reports.payment_comment'),
                Method: t('payments.payment_method')
            }]);
        }
    }, [fullData, monthlyOneTimeForApt, currentTab]);

    useEffect(async () => {
        if (!client) return;
        dispatch(showLoader());
        await loadApartmentData(apartmentNumber);
        const allApartmentsPayments = await loadMonthlyAndOneTime(year || BllinkUtils.BllinkTime.getCurrentYear());
        setMonthlyOneTimeData(allApartmentsPayments);
        dispatch(hideLoader());
        dispatch(changeTab(null));
    }, [buildingID, client]);

    const loadApartmentData = async (apartmentNum) => {
        const response = await client.managers?.reports.getApartmentData(buildingID, apartmentNum) ?? { paymentResults: [], Total: {} };
        BllinkLogger.info(`all buildings payments by apartment cashflow `, response);

        if (response.paymentResults) {
            BllinkLogger.info(`ApartmentCashFlowTable rows are `, response.paymentResults);

            response.paymentResults = response.paymentResults.map((res) => {
                const modifiedRes = {...res, translatedMethod: res.Method ? t(`buildings.${res.Method}`) : ''};
                /*Added due to https://bllink.atlassian.net/jira/software/projects/SSB/boards/7?assignee=63df7122a5d0c826306ba6a5&selectedIssue=SSB-1212*/
                if (constants.APIContract.paymentStates.CANCELED_PAYMENT_STATUSES.includes(res.PaymentState) && modifiedRes.Amount < 0) {
                    modifiedRes.Comment = modifiedRes.Comment?.length > 0 ?  modifiedRes.Comment.concat('-' + t('reports.refund.refund')) : t('reports.refund.refund');
                } else if (res.PaymentState === constants.APIContract.paymentStates.FAILED && modifiedRes.Method === constants.APIContract.paymentMethodsKeys.bankCharge){
                    /*Added due to https://bllink.atlassian.net/jira/software/projects/SSB/boards/7?assignee=63df7122a5d0c826306ba6a5&selectedIssue=SSB-1213*/
                    modifiedRes.Comment = modifiedRes.Comment?.length > 0 ?  modifiedRes.Comment.concat('-' + t('statuses.failed')) : t('statuses.failed');
                }
                return modifiedRes;
            });

            setRows(response.paymentResults);
        }
    };

    const onApartmentChanged = async (newApartmentNumber) => {
        setSelectedApartment(newApartmentNumber);
        BllinkLogger.info(`newApartmentNumber ${newApartmentNumber.apartmentNumber}`);
        history.push(`/${constants.pages.newApartmentReport}/${buildingID}/${newApartmentNumber.apartmentNumber}`);
        dispatch(showLoader());
        await loadApartmentData(newApartmentNumber.apartmentNumber);
        dispatch(hideLoader());
    }

    useMemo(() => setApartmentOptions(currentBuildingData.apartments), [currentBuildingData])
    useMemo(() => {
        const apartmentNum = selectedApartment.apartmentNumber;
        const aptData = monthlyOneTimeData?.[apartmentNum];
        setMonthlyOneTimeForApt(aptData);
    }, [monthlyOneTimeData, selectedApartment]);
    useMemo(() => {
        const apts = currentBuildingData?.apartments ?? [];
        const currentApartment = apts.find(item => item.apartmentNumber === apartmentNumber);
        if (apts.length > 0 && !currentApartment) {
            onApartmentChanged(apts[0]);
        } else {
            setSelectedApartment(currentApartment ?? {});
        }
    }, [currentBuildingData, apartmentNumber])

    async function handlePaymentStop({ apartmentNumber }) {
        return loadApartmentData(apartmentNumber);
    }

    const loadMonthlyAndOneTime = async (year) => {
        const {payload} = await dispatch(fetchPaymentsByYear({
            buildingID,
            year
        }));

        if (payload?.apartments) {
            let {rows, headerExtras} = payload.apartments;
            const apartments = rows.reduce((acc, cur) => ({ ...acc, [cur.apartmentNumber]: {...cur} }), {});

            // return data grouped by apartment
            let data = {};
            for (let key in apartments) {
                const tableRows = [
                    {
                        ...apartments[key],
                        title: t('reports.apartment.fee'),
                        key: 'total'
                    },
                    {
                        ...apartments[key],
                        title: t('reports.apartment.paid'),
                        key: 'paid'
                    },
                    {
                        ...apartments[key],
                        title: t('reports.apartment.diff'),
                        key: 'amount_left'
                    },
                ];

                data[key] = {
                    headerExtras,
                    rows: tableRows
                }
            }

            return data;
        }
    }

    const components = {
        monthly: {
            component: MonthlyData,
            props: {
                data: monthlyOneTimeForApt,
                filters,
                buildingID,
                apartmentNumber: selectedApartment.apartmentNumber,
            }
        },
        oneTime: {
            component: OneTimeData,
            props: {
                data: monthlyOneTimeForApt,
                filters,
                buildingID,
                apartmentNumber: selectedApartment.apartmentNumber,
            }
        },
        full: {
            component: FullReport,
            props: {
                monthlyData: fullData,
                filters,
                apartmentNumber: selectedApartment.apartmentNumber,
                buildingID,
                client,
                onPaymentStop: handlePaymentStop,
            }
        }
    };
    const tabComponent = components[currentTab];

    if (!userCheck) {
        return (<div className="div-buffer-empty-content" />);
    }

    return (
        <StyledReport className={'report'} ltr={ltr}>
            <PageTitle text={t('titles.apartment_report')}>
                {fullData && fullData.length > 0 && (
                    <PrintPdfDropdown
                        className='report-pdf-icons'
                        buildingID={buildingID}
                        eventLabel="apartmentCashflow"
                        titleString={t('reports.apartment_cashflow_title', {apartmentNumber: apartmentNumber})}
                        excelTitle={t('reports.apartment_cashflow_title', {apartmentNumber: apartmentNumber})}
                        reportName={t('titles.apartment_report')}
                        pdfContainer={pdfContainer.current}
                        rows={parsedData}
                        titleRow={titleRow}
                        ignoreColumns={keysToIgnore}
                        address={address}
                        pdfIframe={isAdmin}
                        pdfSettings={{ chunkSize: 15, orientation: 'l' }}
                    />
                )}
            </PageTitle>
            <div className={'report-wrap'}>
                <div className={'report-top'}>
                    <div className='report-top-start'>
                        <div
                            className={'show-details'}
                            onClick={() => setShowTenantsDetails(true)}
                        >
                            <img src={user} alt="tenant details" />
                        </div>
                        <div className="search">
                            <input
                                type="text"
                                name="search"
                                onChange={e => setFilters({
                                    global: e.target.value
                                })}
                            />
                            <img src={Search} alt={t('search.search')} />
                        </div>
                        <Select
                            className={'select'}
                            styles={newSelectStyles}
                            options={apartmentOptions}
                            value={selectedApartment}
                            onChange={onApartmentChanged}
                            isRtl
                        />
                    </div>

                    <div className='report-top-end'>
                        <ReportTabs className={currentTab === 'oneTime' ? 'onetime-selected' : ''}>
                            <SwitchSelector
                                initialSelectedIndex={tab === 'monthly' ? 1 : 2}
                                options={[
                                    {
                                        label: t('reports.apartment.oneTime'),
                                        value: 'oneTime',
                                        selectedBackgroundColor: 'white',
                                    },
                                    {
                                        label: t('reports.apartment.monthly'),
                                        value: 'monthly',
                                        selectedBackgroundColor: 'white',
                                    },
                                    {
                                        label: t('reports.apartment.full'),
                                        value: 'full',
                                        selectedBackgroundColor: 'white',
                                    }
                                ]}
                                selectedFontColor={'#6E74FF'}
                                fontColor={'#ffffff'}
                                backgroundColor={'linear-gradient(90deg, #6C90FF -0.23%, #8567FF 92.15%)'}
                                onChange={evt => setCurrentTab(evt)}
                            />
                        </ReportTabs>
                    </div>
                </div>
            </div>

            <div id="section-to-print" ref={pdfContainer}>
                <tabComponent.component
                    {...tabComponent.props}
                />
            </div>

            {
                selectedApartment?.value && (
                    <CommentsHistory
                        buildingId={buildingID}
                        currentApartment={selectedApartment}
                        hideTitle
                    />
                )
            }

            <TenantDetailsModal
                apartmentNumber={apartmentNumber}
                buildingId={buildingID}
                show={showTenantDetails}
                onHide={() => setShowTenantsDetails(false)}
            />
        </StyledReport>
    );
}
